import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TabBtn } from "@/components/transport/transportShared";
import { VetBadge, PrescriptionDialog, MODE_LABEL, vfmt } from "@/components/vet/vetShared";
import {
  Stethoscope, CalendarCheck2, FileText, IndianRupee, Settings2, ScrollText,
  Plus, Pencil, Archive, ArchiveRestore, Power, BadgeCheck, XCircle,
} from "lucide-react";

const F = ({ label, children }) => (
  <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>
);

function Table({ cols, children, testId }) {
  return (
    <div className="am-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
          {cols.map((c) => <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/* ---------- VETS TAB ---------- */
function VetsTab() {
  const [vets, setVets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => {
    api.get("/vet/vets", { params: { include_archived: true } }).then((r) => setVets(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, experience_years: Number(f.experience_years || 0),
        consultation_fee: Number(f.consultation_fee || 0), home_visit_fee: Number(f.home_visit_fee || 0),
        emergency_fee: Number(f.emergency_fee || 0),
        service_areas: (f.areas_text || "").split(",").map((s) => s.trim()).filter(Boolean) };
      delete body.areas_text;
      if (editing) {
        delete body.mobile;
        await api.patch(`/vet/vets/${editing.vet_id}`, { ...body, rating: f.rating !== undefined ? Number(f.rating) : undefined });
      } else {
        await api.post("/vet/vets", body);
      }
      toast.success(editing ? "Vet updated" : "Vet added (logs in with existing OTP system)");
      setAdding(false); setEditing(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const open = adding || !!editing;

  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="av-add-btn" onClick={() => { setF({ emergency_available: false }); setAdding(true); }}>
        <Plus className="w-4 h-4 mr-1" /> Add Vet
      </Button>
      <Table testId="av-vets-table" cols={["Vet", "Mobile", "Specialization", "Areas", "Fees (C/H/E)", "Verification", "Active", "Actions"]}>
        {vets.map((v) => (
          <tr key={v.vet_id} className={`border-b last:border-0 ${v.is_archived ? "opacity-50" : ""}`} data-testid={`av-row-${v.vet_id}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{v.name}<div className="text-xs font-normal text-muted-foreground">{v.qualification}{v.is_archived ? " · archived" : ""}</div></td>
            <td className="px-3 py-3 font-mono text-xs">{v.mobile}</td>
            <td className="px-3 py-3 whitespace-nowrap">{v.specialization || "—"}</td>
            <td className="px-3 py-3">{(v.service_areas || []).join(", ") || "—"}</td>
            <td className="px-3 py-3 whitespace-nowrap text-xs">{vfmt(v.consultation_fee)} / {vfmt(v.home_visit_fee)} / {vfmt(v.emergency_fee)}</td>
            <td className="px-3 py-3"><VetBadge status={v.verification_status} /></td>
            <td className="px-3 py-3">{v.is_active ? "Yes" : "No"}</td>
            <td className="px-3 py-3">
              <div className="flex gap-1 justify-end flex-wrap">
                {v.verification_status === "PENDING" && (
                  <>
                    <Button size="icon" variant="ghost" title="Verify" data-testid={`av-verify-${v.vet_id}`}
                      onClick={() => act(() => api.post(`/vet/vets/${v.vet_id}/verify`, { status: "VERIFIED" }), "Vet verified")}>
                      <BadgeCheck className="w-4 h-4 text-emerald-600" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Reject" data-testid={`av-reject-${v.vet_id}`}
                      onClick={() => act(() => api.post(`/vet/vets/${v.vet_id}/verify`, { status: "REJECTED" }), "Verification rejected")}>
                      <XCircle className="w-4 h-4 text-destructive" />
                    </Button>
                  </>
                )}
                <Button size="icon" variant="ghost" title="Edit" data-testid={`av-edit-${v.vet_id}`}
                  onClick={() => { setF({ ...v, areas_text: (v.service_areas || []).join(", ") }); setEditing(v); }}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" title={v.is_active ? "Deactivate" : "Activate"} data-testid={`av-toggle-${v.vet_id}`}
                  onClick={() => act(() => api.patch(`/vet/vets/${v.vet_id}`, { is_active: !v.is_active }), v.is_active ? "Deactivated" : "Activated")}>
                  <Power className={`w-4 h-4 ${v.is_active ? "text-emerald-600" : "text-muted-foreground"}`} />
                </Button>
                {!v.is_archived
                  ? <Button size="icon" variant="ghost" title="Archive" data-testid={`av-archive-${v.vet_id}`} onClick={() => act(() => api.post(`/vet/vets/${v.vet_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                  : <Button size="icon" variant="ghost" title="Restore" data-testid={`av-restore-${v.vet_id}`} onClick={() => act(() => api.post(`/vet/vets/${v.vet_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <Dialog open={open} onOpenChange={() => { setAdding(false); setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="av-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Vet" : "Add Vet"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Full Name"><Input className="am-input" data-testid="av-f-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></F>
            {!editing && <F label="Mobile (single login)"><Input className="am-input" data-testid="av-f-mobile" value={f.mobile || ""} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></F>}
            <F label="Qualification"><Input className="am-input" data-testid="av-f-qual" value={f.qualification || ""} onChange={(e) => setF({ ...f, qualification: e.target.value })} /></F>
            <F label="Vet Registration No."><Input className="am-input" data-testid="av-f-reg" value={f.registration_number || ""} onChange={(e) => setF({ ...f, registration_number: e.target.value })} /></F>
            <F label="Specialization"><Input className="am-input" data-testid="av-f-spec" value={f.specialization || ""} onChange={(e) => setF({ ...f, specialization: e.target.value })} /></F>
            <F label="Experience (yrs)"><Input type="number" className="am-input" value={f.experience_years ?? ""} onChange={(e) => setF({ ...f, experience_years: e.target.value })} /></F>
            <F label="Clinic / Hospital"><Input className="am-input" value={f.clinic_name || ""} onChange={(e) => setF({ ...f, clinic_name: e.target.value })} /></F>
            <F label="Email"><Input className="am-input" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></F>
            <F label="Consultation Fee ₹"><Input type="number" className="am-input" data-testid="av-f-cfee" value={f.consultation_fee ?? ""} onChange={(e) => setF({ ...f, consultation_fee: e.target.value })} /></F>
            <F label="Home Visit Fee ₹"><Input type="number" className="am-input" value={f.home_visit_fee ?? ""} onChange={(e) => setF({ ...f, home_visit_fee: e.target.value })} /></F>
            <F label="Emergency Fee ₹"><Input type="number" className="am-input" value={f.emergency_fee ?? ""} onChange={(e) => setF({ ...f, emergency_fee: e.target.value })} /></F>
            {editing && <F label="Rating (0-5)"><Input type="number" step="0.1" className="am-input" data-testid="av-f-rating" value={f.rating ?? ""} onChange={(e) => setF({ ...f, rating: e.target.value })} /></F>}
          </div>
          <F label="Address"><Input className="am-input" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} /></F>
          <F label="Service Areas (comma separated)"><Input className="am-input" data-testid="av-f-areas" value={f.areas_text || ""} onChange={(e) => setF({ ...f, areas_text: e.target.value })} /></F>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <Switch checked={!!f.emergency_available} onCheckedChange={(v) => setF({ ...f, emergency_available: v })} data-testid="av-f-emergency" /> Emergency Service Available
          </label>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setAdding(false); setEditing(null); }}>Cancel</Button>
            <Button className="am-btn-primary" disabled={busy} data-testid="av-f-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- APPOINTMENTS TAB ---------- */
function AppointmentsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/vet/appointments").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Table testId="av-appts-table" cols={["When", "Client", "Animal", "Mode", "Symptoms", "Fee", "Status"]}>
      {rows.map((a) => (
        <tr key={a.appointment_id} className="border-b last:border-0">
          <td className="px-3 py-3 whitespace-nowrap text-xs">{a.date} {a.time}</td>
          <td className="px-3 py-3 font-bold whitespace-nowrap">{a.client_name}</td>
          <td className="px-3 py-3 whitespace-nowrap">{[a.animal_info?.type, a.animal_info?.breed].filter(Boolean).join(" ") || "—"}</td>
          <td className="px-3 py-3 whitespace-nowrap">{MODE_LABEL[a.mode]}</td>
          <td className="px-3 py-3 max-w-[220px] truncate">{a.symptoms}</td>
          <td className="px-3 py-3 whitespace-nowrap">{vfmt(a.fee)}</td>
          <td className="px-3 py-3"><VetBadge status={a.status} /></td>
        </tr>
      ))}
    </Table>
  );
}

/* ---------- PRESCRIPTIONS TAB ---------- */
function PrescriptionsTab() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => { api.get("/vet/prescriptions").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <>
      <Table testId="av-rx-table" cols={["Date", "Vet", "Owner", "Animal", "Diagnosis", ""]}>
        {rows.map((rx) => (
          <tr key={rx.prescription_id} className="border-b last:border-0">
            <td className="px-3 py-3 whitespace-nowrap text-xs">{(rx.created_at || "").slice(0, 10)}</td>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{rx.vet_details?.name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{rx.owner_name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{[rx.animal_info?.type, rx.animal_info?.breed].filter(Boolean).join(" ") || "—"}</td>
            <td className="px-3 py-3 max-w-[220px] truncate">{rx.diagnosis}</td>
            <td className="px-3 py-3 text-right">
              <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`av-rx-view-${rx.prescription_id}`} onClick={() => setView(rx)}>View</Button>
            </td>
          </tr>
        ))}
      </Table>
      <PrescriptionDialog rx={view} open={!!view} setOpen={() => setView(null)} />
    </>
  );
}

/* ---------- PAYMENTS TAB ---------- */
function PaymentsTab() {
  const [data, setData] = useState(null);
  const [paidFor, setPaidFor] = useState(null);
  const [txn, setTxn] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => { api.get("/vet/payments").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const s = data?.summary;
  return (
    <div className="space-y-4">
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="av-pay-kpis">
          {[["Total Fees", vfmt(s.total_fee)], ["Platform Commission", vfmt(s.total_commission)], ["Vet Payouts", vfmt(s.total_payout)], ["Pending", vfmt(s.pending)], ["Paid", vfmt(s.paid)]].map(([l, v]) => (
            <div key={l} className="am-card p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{l}</div><div className="font-display text-xl font-extrabold mt-1">{v}</div></div>
          ))}
        </div>
      )}
      <Table testId="av-pay-table" cols={["Vet", "Client", "Mode", "Fee", "Comm %", "Commission", "Vet Payout", "Status", ""]}>
        {(data?.payments || []).map((p) => (
          <tr key={p.payment_id} className="border-b last:border-0" data-testid={`av-pay-row-${p.payment_id}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{p.vet_name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{p.client_name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{MODE_LABEL[p.mode]}</td>
            <td className="px-3 py-3">{vfmt(p.fee)}</td>
            <td className="px-3 py-3">{p.commission_percent}%</td>
            <td className="px-3 py-3">{vfmt(p.commission)}</td>
            <td className="px-3 py-3 font-bold">{vfmt(p.vet_payout)}</td>
            <td className="px-3 py-3"><VetBadge status={p.status} />{p.transaction_reference ? <div className="text-[10px] text-muted-foreground">ref: {p.transaction_reference}</div> : null}</td>
            <td className="px-3 py-3 text-right">
              {p.status === "PENDING" && (
                <Button size="sm" className="am-btn-primary rounded-full text-xs" data-testid={`av-pay-mark-${p.payment_id}`} onClick={() => { setPaidFor(p); setTxn(""); }}>Mark Paid</Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <Dialog open={!!paidFor} onOpenChange={() => setPaidFor(null)}>
        <DialogContent className="max-w-sm" data-testid="av-pay-dialog">
          <DialogHeader><DialogTitle>Pay {paidFor && vfmt(paidFor.vet_payout)} to {paidFor?.vet_name}</DialogTitle></DialogHeader>
          <Input className="am-input" placeholder="Transaction reference" data-testid="av-pay-txn" value={txn} onChange={(e) => setTxn(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPaidFor(null)}>Cancel</Button>
            <Button className="am-btn-primary" disabled={busy || !txn.trim()} data-testid="av-pay-confirm"
              onClick={async () => {
                setBusy(true);
                try { await api.post(`/vet/payments/${paidFor.payment_id}/mark-paid`, { transaction_reference: txn.trim() }); toast.success("Paid"); setPaidFor(null); refresh(); }
                catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                finally { setBusy(false); }
              }}>Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- SETTINGS TAB ---------- */
function SettingsTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/vet/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/vet/settings", {
        verification_required: !!s.verification_required,
        consultation_commission_percent: Number(s.consultation_commission_percent),
        home_visit_commission_percent: Number(s.home_visit_commission_percent),
        emergency_commission_percent: Number(s.emergency_commission_percent),
        cancellation_hours: Number(s.cancellation_hours),
      });
      toast.success("Vet settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const N = ({ k, label }) => (
    <F label={label}><Input type="number" className="am-input" data-testid={`av-set-${k}`} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></F>
  );
  return (
    <div className="am-card p-6 max-w-xl space-y-4" data-testid="av-settings">
      <h2 className="font-display text-lg font-bold">Vet Module Configuration</h2>
      <div className="grid grid-cols-2 gap-3">
        <N k="consultation_commission_percent" label="Consultation Commission %" />
        <N k="home_visit_commission_percent" label="Home Visit Commission %" />
        <N k="emergency_commission_percent" label="Emergency Commission %" />
        <N k="cancellation_hours" label="Cancellation Window (hrs)" />
      </div>
      <label className="flex items-center gap-2 text-sm font-semibold">
        <Switch checked={!!s.verification_required} onCheckedChange={(v) => setS({ ...s, verification_required: v })} data-testid="av-set-verification" />
        Vet verification required before bookings
      </label>
      <Button className="am-btn-primary" disabled={busy} data-testid="av-set-save" onClick={save}>Save Settings</Button>
    </div>
  );
}

/* ---------- AUDIT TAB ---------- */
function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/vet/audit-logs").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Table testId="av-audit-table" cols={["Time", "Role", "Action", "Entity", "Entity ID"]}>
      {rows.map((l) => (
        <tr key={l.log_id} className="border-b last:border-0">
          <td className="px-3 py-3 whitespace-nowrap text-xs">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</td>
          <td className="px-3 py-3">{l.user_role}</td>
          <td className="px-3 py-3 font-bold whitespace-nowrap">{l.action}</td>
          <td className="px-3 py-3">{l.entity_type}</td>
          <td className="px-3 py-3 font-mono text-xs">{(l.entity_id || "").slice(0, 10)}</td>
        </tr>
      ))}
    </Table>
  );
}

/* ---------- PAGE ---------- */
export default function AdminVets() {
  const [tab, setTab] = useState("vets");
  return (
    <DashboardShell title="Vet Management" subtitle="Verification, appointments, payouts & configuration">
      <div className="flex gap-2 flex-wrap mb-6">
        <TabBtn testId="av-tab-vets" active={tab === "vets"} onClick={() => setTab("vets")} icon={Stethoscope}>Vets</TabBtn>
        <TabBtn testId="av-tab-appointments" active={tab === "appointments"} onClick={() => setTab("appointments")} icon={CalendarCheck2}>Appointments</TabBtn>
        <TabBtn testId="av-tab-prescriptions" active={tab === "prescriptions"} onClick={() => setTab("prescriptions")} icon={FileText}>Prescriptions</TabBtn>
        <TabBtn testId="av-tab-payments" active={tab === "payments"} onClick={() => setTab("payments")} icon={IndianRupee}>Payments</TabBtn>
        <TabBtn testId="av-tab-settings" active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings2}>Settings</TabBtn>
        <TabBtn testId="av-tab-audit" active={tab === "audit"} onClick={() => setTab("audit")} icon={ScrollText}>Audit</TabBtn>
      </div>
      {tab === "vets" && <VetsTab />}
      {tab === "appointments" && <AppointmentsTab />}
      {tab === "prescriptions" && <PrescriptionsTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "audit" && <AuditTab />}
    </DashboardShell>
  );
}
